import { useEffect, useRef } from 'react'
import type { VisualState } from 'types'

interface UseResearchVisualAnimationArgs {
  isResearching: boolean
  setVisualState: React.Dispatch<React.SetStateAction<VisualState>>
}

/** Lead-research canvas animation — network → molecule → neural_network progression. */
export function useResearchVisualAnimation({
  isResearching,
  setVisualState,
}: UseResearchVisualAnimationArgs) {
  const researchIntervalRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (isResearching) {
      let stage: 'network' | 'molecule' | 'neural_network' = 'network'

      setVisualState(prev => ({
        ...prev,
        researchActive: true,
        shape: stage,
      }))

      const stage1Timer = setTimeout(() => {
        stage = 'molecule'
        setVisualState(prev => ({ ...prev, shape: stage }))
      }, 1500)

      const stage2Timer = setTimeout(() => {
        stage = 'neural_network'

        const nodePositions = [
          { x: 0.2, y: 0.5 },
          { x: 0.35, y: 0.3 },
          { x: 0.5, y: 0.5 },
          { x: 0.65, y: 0.3 },
          { x: 0.8, y: 0.5 },
          { x: 0.9, y: 0.4 },
        ]
        const initialNodes = nodePositions.map((pos, idx) => ({
          ...pos,
          status: idx === 0 ? ('active' as const) : ('pending' as const),
        }))

        setVisualState(prev => ({
          ...prev,
          shape: stage,
          researchState: { nodes: initialNodes },
        }))

        let currentNode = 0
        const progressInterval = setInterval(() => {
          currentNode++
          if (currentNode >= nodePositions.length) {
            clearInterval(progressInterval)
            return
          }
          setVisualState(prev => {
            const currentNodes = prev.researchState?.nodes || initialNodes
            const updatedNodes = currentNodes.map((node, idx) => ({
              ...node,
              status:
                idx < currentNode
                  ? ('completed' as const)
                  : idx === currentNode
                    ? ('active' as const)
                    : ('pending' as const),
            }))
            return { ...prev, researchState: { nodes: updatedNodes } }
          })
        }, 800)

        researchIntervalRef.current = progressInterval
      }, 3000)

      return () => {
        clearTimeout(stage1Timer)
        clearTimeout(stage2Timer)
        if (researchIntervalRef.current) {
          clearInterval(researchIntervalRef.current)
          researchIntervalRef.current = null
        }
      }
    }

    setVisualState(prev => {
      if (prev.researchState?.nodes?.length) {
        const completedNodes = prev.researchState.nodes.map(node => ({
          ...node,
          status: 'completed' as const,
        }))
        return { ...prev, researchActive: false, researchState: { nodes: completedNodes } }
      }
      return { ...prev, researchActive: false }
    })

    const clearTimer = setTimeout(() => {
      setVisualState(prev => ({ ...prev, researchState: { nodes: [] } }))
    }, 2000)

    return () => clearTimeout(clearTimer)
  }, [isResearching, setVisualState])

  return { researchIntervalRef }
}
